import { useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  Icon,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { docParsing } from "@/api/docParsing.service";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { useMutation } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { useSharePointBrowser } from "@/hooks/useSharePointBrowser";
import { formatDateShort } from "@/utils/dateFormatter";
import { Breadcrumb } from "./FolderBrowser";
import { JsonModal } from "./JsonModal";
import ParsingJobsPage from "../ParsingJobs/index";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import styles from "./DocumentParsing.module.css";

// ---------- Types ----------

type BrowserFolderItem = {
  type: "folder";
  id: string;
  name: string;
  path: string;
  itemCount: number;
  timeLastModified: string;
};

type BrowserFileItem = {
  type: "file";
  id: string;
  name: string;
  size: number;
  timeLastModified: string;
  parseStatus: string | null;
  parseResultId: string | null;
  sharepointFileId: string;
  fileUrl: string;
  submissionEntryFileId: string | null;
};

type BrowserItem = BrowserFolderItem | BrowserFileItem;

type ReplaceTarget = {
  sharepointFileId: string;
  submissionEntryFileId: string;
};

// ---------- Sub-components ----------

const STATUS_CONFIG: Record<string, { className: string; icon: string; label: string }> = {
  parsed: { className: "statusParsed", icon: "\u2713", label: "Parsed" },
  queued: { className: "statusProcessing", icon: "\u23F3", label: "Queued" },
  processing: { className: "statusProcessing", icon: "\u21BB", label: "Processing" },
  failed: { className: "statusFailed", icon: "\u2717", label: "Failed" },
  waiting: { className: "statusWaiting", icon: "\u23F8", label: "Waiting" },
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className={styles.noStatus}>—</span>;

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.waiting;

  return (
    <Chip
      size="small"
      variant="filled"
      className={styles[config.className as keyof typeof styles] || styles.statusWaiting}
    >
      <span className={styles.statusContent}>
        <span className={styles.statusIcon}>{config.icon}</span>
        {config.label}
      </span>
    </Chip>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

const PARSEABLE_EXTENSIONS = new Set([".doc", ".docx"]);

function isParseable(fileName: string): boolean {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) return false;
  return PARSEABLE_EXTENSIONS.has(fileName.slice(dotIndex).toLowerCase());
}

const DOWNLOADABLE_EXTENSIONS = new Set([".docx"]);

function isDownloadable(fileName: string): boolean {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) return false;
  return DOWNLOADABLE_EXTENSIONS.has(fileName.slice(dotIndex).toLowerCase());
}

// ---------- SharedDocumentsTab Component ----------

function SharedDocumentsTab() {
  const { currentPath, folders, files, loading, error, navigateTo, goUp, canGoUp, refetch } =
    useSharePointBrowser();

  const {
    mutate: parseFile,
    error: parseError,
    reset: resetParseError,
  } = useMutation(docParsing.parseFile);
  const { hasPermission } = usePermissions();
  const canDownload = hasPermission("submission.parsing.download");
  const canUpload = hasPermission("submission.parsing.upload");
  const [jsonModalResultId, setJsonModalResultId] = useState<string | null>(null);
  const [parsingFileId, setParsingFileId] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<Error | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<ReplaceTarget | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetReplaceTarget = () => {
    setReplaceTarget(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (item: BrowserFileItem) => {
    setDownloadingFileId(item.sharepointFileId);
    try {
      await docParsing.downloadFile(item.sharepointFileId, item.name);
    } catch (err) {
      setDownloadError(err instanceof Error ? err : new Error("Download failed"));
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleParse = async (item: BrowserFileItem) => {
    setParsingFileId(item.sharepointFileId);
    try {
      await parseFile({
        sharepointFileId: item.sharepointFileId,
        fileName: item.name,
        submissionEntryFileId: item.submissionEntryFileId ?? undefined,
      });
      refetch();
    } finally {
      setParsingFileId(null);
    }
  };

  const handleReplace = (item: BrowserFileItem) => {
    if (!item.submissionEntryFileId) {
      return;
    }

    setReplaceTarget({
      sharepointFileId: item.sharepointFileId,
      submissionEntryFileId: item.submissionEntryFileId,
    });
    fileInputRef.current?.click();
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !replaceTarget) {
      resetReplaceTarget();
      return;
    }
    if (!file.name.toLowerCase().endsWith(".docx")) {
      setToast({ message: "Only .docx files are supported for replace.", type: "error" });
      resetReplaceTarget();
      return;
    }
    try {
      await docParsing.replaceFile(
        replaceTarget.sharepointFileId,
        file,
        replaceTarget.submissionEntryFileId,
      );
      setToast({ message: `File replaced successfully: ${file.name}`, type: "success" });
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Replace failed";
      setToast({ message, type: "error" });
    } finally {
      resetReplaceTarget();
    }
  };

  // Merge folders and files into a single list — folders first, then files
  const browserItems: BrowserItem[] = useMemo(() => {
    const folderItems: BrowserItem[] = folders.map(f => ({
      type: "folder" as const,
      id: `folder-${f.serverRelativeUrl}`,
      name: f.name,
      path: `${currentPath}/${f.name}`,
      itemCount: f.itemCount,
      timeLastModified: f.timeLastModified,
    }));

    const fileItems: BrowserItem[] = files.map(f => ({
      type: "file" as const,
      id: f.sharepointFileId,
      name: f.name,
      size: f.size,
      timeLastModified: f.timeLastModified,
      parseStatus: f.parseStatus,
      parseResultId: f.parseResultId,
      sharepointFileId: f.sharepointFileId,
      fileUrl: f.fileUrl,
      submissionEntryFileId: f.submissionEntryFileId,
    }));

    return [...folderItems, ...fileItems];
  }, [folders, files, currentPath]);

  const handleRowClick = (row: BrowserItem) => {
    if (row.type === "folder") {
      resetParseError();
      setDownloadError(null);
      navigateTo(row.path);
    }
  };

  const columns: Column<BrowserItem>[] = [
    {
      header: "Name",
      accessor: "name",
      render: (_value: unknown, row: BrowserItem) => (
        <span className={row.type === "folder" ? styles.folderName : undefined}>
          <Icon icon={row.type === "folder" ? "chevron-right" : "file"} size="small" />
          <span>{row.name}</span>
        </span>
      ),
    },
    {
      header: "Size",
      accessor: "type",
      render: (_value: unknown, row: BrowserItem) => {
        if (row.type === "folder") return `${row.itemCount} items`;
        return formatBytes(row.size);
      },
    },
    {
      header: "Modified",
      accessor: "id",
      render: (_value: unknown, row: BrowserItem) => {
        if (!row.timeLastModified) return "-";
        return formatDateShort(row.timeLastModified);
      },
    },
    {
      header: "Parse Status",
      accessor: "name",
      render: (_value: unknown, row: BrowserItem) => {
        if (row.type === "folder") return null;
        if (!isParseable(row.name)) return null;
        return <StatusBadge status={row.parseStatus} />;
      },
    },
    {
      header: "Actions",
      accessor: "name",
      render: (_value: unknown, row: BrowserItem) => {
        if (row.type === "folder") return null;
        if (!isParseable(row.name)) return null;

        const isThisFileParsing = parsingFileId === row.sharepointFileId;
        const isThisFileUploading = replaceTarget?.sharepointFileId === row.sharepointFileId;
        const canReplaceFile = canUpload && isDownloadable(row.name) && !!row.submissionEntryFileId;
        const canParseFile = !!row.submissionEntryFileId;

        if (row.parseStatus === "processing" || isThisFileParsing) {
          return (
            <div className={styles.actionsCell}>
              <Button size="small" variant="outlined" disabled>
                <span className={styles.btnContent}>{"\u21BB"} Processing...</span>
              </Button>
              {canDownload && isDownloadable(row.name) && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={downloadingFileId === row.sharepointFileId}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleDownload(row);
                  }}
                >
                  <span className={styles.btnContent}>
                    {downloadingFileId === row.sharepointFileId
                      ? "\u23F3 Downloading..."
                      : "\u2B07 Download"}
                  </span>
                </Button>
              )}
              {canReplaceFile && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isThisFileUploading}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleReplace(row);
                  }}
                >
                  <span className={styles.btnContent}>
                    {isThisFileUploading ? "Uploading..." : "\u21A5 Replace"}
                  </span>
                </Button>
              )}
            </div>
          );
        }

        if (row.parseStatus === "parsed" && row.parseResultId) {
          return (
            <div className={styles.actionsCell}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setJsonModalResultId(row.parseResultId);
                }}
              >
                <span className={styles.btnContent}>{"\u007B\u007D"} View JSON</span>
              </Button>
              {canParseFile && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleParse(row);
                  }}
                >
                  <span className={styles.btnContent}>{"\u21BB"} Re-parse</span>
                </Button>
              )}
              {canDownload && isDownloadable(row.name) && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={downloadingFileId === row.sharepointFileId}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleDownload(row);
                  }}
                >
                  <span className={styles.btnContent}>
                    {downloadingFileId === row.sharepointFileId
                      ? "\u23F3 Downloading..."
                      : "\u2B07 Download"}
                  </span>
                </Button>
              )}
              {canReplaceFile && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isThisFileUploading}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleReplace(row);
                  }}
                >
                  <span className={styles.btnContent}>
                    {isThisFileUploading ? "Uploading..." : "\u21A5 Replace"}
                  </span>
                </Button>
              )}
            </div>
          );
        }

        if (row.parseStatus === "failed" || row.parseStatus === "queued") {
          return (
            <div className={styles.actionsCell}>
              {canParseFile && (
                <Button
                  size="small"
                  variant="outlined"
                  color={row.parseStatus === "failed" ? "error" : "warning"}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleParse(row);
                  }}
                >
                  <span className={styles.btnContent}>{"\u21BB"} Retry</span>
                </Button>
              )}
              {canDownload && isDownloadable(row.name) && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={downloadingFileId === row.sharepointFileId}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleDownload(row);
                  }}
                >
                  <span className={styles.btnContent}>
                    {downloadingFileId === row.sharepointFileId
                      ? "\u23F3 Downloading..."
                      : "\u2B07 Download"}
                  </span>
                </Button>
              )}
              {canReplaceFile && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isThisFileUploading}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleReplace(row);
                  }}
                >
                  <span className={styles.btnContent}>
                    {isThisFileUploading ? "Uploading..." : "\u21A5 Replace"}
                  </span>
                </Button>
              )}
            </div>
          );
        }

        return (
          <div className={styles.actionsCell}>
            {canParseFile && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleParse(row);
                }}
              >
                <span className={styles.btnContent}>{"\u21BB"} Parse</span>
              </Button>
            )}
            {canDownload && isDownloadable(row.name) && (
              <Button
                size="small"
                variant="outlined"
                disabled={downloadingFileId === row.sharepointFileId}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleDownload(row);
                }}
              >
                <span className={styles.btnContent}>
                  {downloadingFileId === row.sharepointFileId
                    ? "\u23F3 Downloading..."
                    : "\u2B07 Download"}
                </span>
              </Button>
            )}
            {canReplaceFile && (
              <Button
                size="small"
                variant="outlined"
                disabled={isThisFileUploading}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleReplace(row);
                }}
              >
                <span className={styles.btnContent}>
                  {isThisFileUploading ? "Uploading..." : "\u21A5 Replace"}
                </span>
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const itemCount = folders.length + files.length;

  return (
    <>
      <input
        type="file"
        accept=".docx"
        ref={fileInputRef}
        onChange={onFileSelected}
        style={{ display: "none" }}
      />

      <div className={styles.breadcrumbRow}>
        <Breadcrumb
          path={currentPath}
          onNavigate={path => {
            resetParseError();
            setDownloadError(null);
            navigateTo(path);
          }}
          onGoUp={() => {
            resetParseError();
            setDownloadError(null);
            goUp();
          }}
          canGoUp={canGoUp}
        />
        {!loading && !error && itemCount > 0 && (
          <span className={styles.breadcrumbCount}>
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </span>
        )}
      </div>

      {loading && <LoadingState message="Loading SharePoint contents..." />}

      {error && !loading && (
        <EmptyState
          variant="error"
          title="Failed to load SharePoint"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => {
              refetch();
            },
          }}
        />
      )}

      {parseError && (
        <div className={styles.parseErrorBanner} role="alert">
          <span>
            <strong>Failed to parse file:</strong> {parseError.message}
          </span>
          <button
            type="button"
            className={styles.parseErrorDismiss}
            aria-label="Dismiss error"
            onClick={resetParseError}
          >
            &times;
          </button>
        </div>
      )}

      {downloadError && (
        <div className={styles.parseErrorBanner} role="alert">
          <span>
            <strong>Failed to download file:</strong> {downloadError.message}
          </span>
          <button
            type="button"
            className={styles.parseErrorDismiss}
            aria-label="Dismiss error"
            onClick={() => setDownloadError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {!loading && !error && itemCount === 0 && (
        <EmptyState
          variant="empty"
          title="Empty folder"
          message="This folder contains no subfolders or files."
        />
      )}

      {!loading && !error && itemCount > 0 && (
        <DataTable
          data={browserItems}
          columns={columns}
          size="small"
          rowKey="id"
          onRowClick={handleRowClick}
        />
      )}

      <JsonModal resultId={jsonModalResultId} onClose={() => setJsonModalResultId(null)} />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </>
  );
}

function DocumentParsingPage() {
  const { hasPermission } = usePermissions();
  const canSeeJobs = hasPermission("submission.parsing.jobs");
  const canSeeShared = hasPermission("submission.parsing.sharepoint");

  const availableTabs = [
    ...(canSeeJobs ? [{ id: "jobs" as const, label: "Jobs" }] : []),
    ...(canSeeShared ? [{ id: "shared-documents" as const, label: "Shared documents" }] : []),
  ];

  const [activeTab, setActiveTab] = useState<"jobs" | "shared-documents">(
    canSeeJobs ? "jobs" : "shared-documents",
  );
  if (availableTabs.length === 0) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Document Parsing" />
      </div>
      {availableTabs.length > 1 && (
        <div className={styles.tabs}>
          {availableTabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {activeTab === "jobs" && <ParsingJobsPage />}
      {activeTab === "shared-documents" && <SharedDocumentsTab />}
    </div>
  );
}

export default DocumentParsingPage;
